import { apiRequest } from '../../shared/api';

// ─── Types ────────────────────────────────────────────────────────────────────

export type RoleType = 'SYSTEM' | 'CUSTOM';
export type ScopeType = 'GLOBAL' | 'ORGANIZATION' | 'BUSINESS_UNIT' | 'DEPARTMENT' | 'LOCATION';
export type ConfidentialityLevel = 'PUBLIC' | 'INTERNAL' | 'RESTRICTED' | 'CONFIDENTIAL' | 'SECRET';
/** Task 016: 14 canonical permission action types */
export type PermissionAction =
  | 'VIEW' | 'CREATE' | 'EDIT' | 'DELETE' | 'ASSIGN'
  | 'APPROVE' | 'REJECT' | 'PUBLISH' | 'EXPORT'
  | 'ARCHIVE' | 'VERIFY' | 'CLOSE' | 'REOPEN' | 'ADMIN';

/** Task 017: ALLOW/DENY effect */
export type PermissionEffect = 'ALLOW' | 'DENY';

export interface Role {
  id: string;
  code: string;
  name: string;
  description?: string;
  roleType: RoleType;
  category?: string;
  isSystem: boolean;
  isActive: boolean;
  isAssignable?: boolean;
  /** Task 007: Configurable max confidentiality level */
  maxConfidentialityLevel?: ConfidentialityLevel;
  /** Task 010: NULL for SYSTEM roles; UUID for CUSTOM roles */
  organizationId?: string | null;
  status?: 'ACTIVE' | 'INACTIVE' | 'ARCHIVED';
  versionNumber?: number;
  createdAt: string;
  updatedAt: string;
  permissions?: Permission[];
}

export interface RoleCategory {
  code: string;
  name: string;
  description: string;
}

/** Task 015: Full canonical permission schema */
export interface Permission {
  id: string;
  code: string;
  /** Task 015: Canonical code — resourceType_action format */
  permissionCode?: string;
  module: string;
  /** Task 015: e.g. OCCURRENCE, RISK, MOC, DOCUMENT */
  resourceType?: string;
  action: string;
  description?: string;
  /** Task 015 / 016: category of the permission */
  category?: string;
  /** Task 015: true for built-in system permissions */
  isSystem?: boolean;
  createdAt?: string;
}

/** Task 017: Full role_permissions entry with effect + conditions */
export interface RolePermissionEntry {
  id: string;
  roleId: string;
  permissionId: string;
  /** Task 017: ALLOW (default) or DENY */
  effect: PermissionEffect;
  /** Task 017: Optional JSONB conditions */
  conditions?: Record<string, any> | null;
  createdAt: string;
}

/** Task 011: Role version snapshot */
export interface RoleVersion {
  id: string;
  roleId: string;
  versionNumber: number;
  roleName?: string;
  description?: string;
  permissionSnapshot: string[];
  effectiveFrom: string;
  effectiveTo?: string | null;
  createdAt: string;
}

/** Task 012/013: Role inheritance link */
export interface RoleInheritance {
  id: string;
  roleId: string;
  inheritedRoleId: string;
  effectiveFrom?: string;
  effectiveTo?: string | null;
  createdAt: string;
  inheritedRole?: Role;
}

export interface RoleAssignment {
  id: string;
  userId: string;
  roleId: string;
  scopeType: ScopeType;
  scopeId?: string | null;
  validFrom: string;
  validTo?: string | null;
  isActive: boolean;
  role?: Role;
}

export interface AuthorizationDecision {
  allowed: boolean;
  reason?: string;
  matchedRole?: string;
  matchedScope?: ScopeType;
  denialStage?: string;
  timestamp: string;
}

/** Task 018: 4-tier ALLOW/DENY precedence result */
export interface PrecedenceDecision {
  decision: 'ALLOW' | 'DENY';
  tier: 1 | 2 | 3 | 4;
  tierName: 'EXPLICIT_DENY' | 'EXPLICIT_ALLOW' | 'INHERITED_ALLOW' | 'DEFAULT_DENY';
  reason: string;
}

export interface RoleAuditLog {
  id: string;
  entityType: string;
  entityId: string;
  action: string;
  changedBy?: string;
  oldState?: any;
  newState?: any;
  createdAt: string;
}

/** Task 002: Organizational Position */
export interface OrganizationalPosition {
  id: string;
  code: string;
  title: string;
  departmentId?: string;
  isActive: boolean;
  createdAt: string;
}

// ─── Service ──────────────────────────────────────────────────────────────────

export const roleService = {
  // ── Roles CRUD ────────────────────────────────────────────────────────────

  async getRoles(filter?: { category?: string; organizationId?: string }): Promise<Role[]> {
    const params = new URLSearchParams();
    if (filter?.category) params.set('category', filter.category);
    if (filter?.organizationId !== undefined) params.set('organizationId', filter.organizationId ?? '');
    const qs = params.toString();
    return apiRequest<Role[]>(`/roles${qs ? `?${qs}` : ''}`);
  },

  async listRoles(filter?: { category?: string; organizationId?: string }): Promise<Role[]> {
    return this.getRoles(filter);
  },

  /** Task 008: Get all 16 canonical role categories */
  async getRoleCategories(): Promise<RoleCategory[]> {
    return apiRequest<RoleCategory[]>('/roles/categories');
  },

  async getRoleById(id: string): Promise<Role> {
    return apiRequest<Role>(`/roles/${id}`);
  },

  async createRole(data: {
    code: string;
    name: string;
    description?: string;
    roleType?: RoleType;
    category?: string;
    /** Task 010: set for CUSTOM roles */
    organizationId?: string;
    /** Task 007: configurable max confidentiality level */
    maxConfidentialityLevel?: ConfidentialityLevel;
    isAssignable?: boolean;
  }): Promise<Role> {
    return apiRequest<Role>('/roles', { method: 'POST', body: JSON.stringify(data) });
  },

  async updateRole(
    id: string,
    updates: {
      name?: string;
      description?: string;
      isActive?: boolean;
      /** Task 007: configurable max confidentiality level */
      maxConfidentialityLevel?: ConfidentialityLevel;
      /** Task 007: configurable assignment flag */
      isAssignable?: boolean;
      status?: 'ACTIVE' | 'INACTIVE' | 'ARCHIVED';
    },
  ): Promise<Role> {
    return apiRequest<Role>(`/roles/${id}`, { method: 'PUT', body: JSON.stringify(updates) });
  },

  async deleteRole(id: string): Promise<{ success: boolean; message: string }> {
    return apiRequest<{ success: boolean; message: string }>(`/roles/${id}`, { method: 'DELETE' });
  },

  // ── Task 011: Role Versioning ──────────────────────────────────────────────

  async getRoleVersions(roleId: string): Promise<RoleVersion[]> {
    return apiRequest<RoleVersion[]>(`/roles/${roleId}/versions`);
  },

  async getRoleVersionAt(roleId: string, timestamp: string): Promise<RoleVersion> {
    return apiRequest<RoleVersion>(`/roles/${roleId}/versions/at?timestamp=${encodeURIComponent(timestamp)}`);
  },

  async createRoleVersionSnapshot(
    roleId: string,
    data?: { reason?: string; effectiveFrom?: string },
  ): Promise<RoleVersion> {
    return apiRequest<RoleVersion>(`/roles/${roleId}/versions`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  },

  async createRoleSnapshot(roleId: string, reason?: string): Promise<RoleVersion> {
    return this.createRoleVersionSnapshot(roleId, { reason });
  },

  // ── Task 012/013: Role Inheritance ─────────────────────────────────────────

  async getEffectivePermissions(roleId: string): Promise<Permission[]> {
    return apiRequest<Permission[]>(`/roles/${roleId}/effective-permissions`);
  },

  async getInheritedRoles(roleId: string): Promise<Role[]> {
    return apiRequest<Role[]>(`/roles/${roleId}/inherited-roles`);
  },

  async addRoleInheritance(
    roleId: string,
    inheritedRoleId: string,
    options?: { effectiveFrom?: string; effectiveTo?: string },
  ): Promise<RoleInheritance> {
    return apiRequest<RoleInheritance>(`/roles/${roleId}/inheritances`, {
      method: 'POST',
      body: JSON.stringify({ inheritedRoleId, ...options }),
    });
  },

  async removeRoleInheritance(roleId: string, inheritedRoleId: string): Promise<void> {
    return apiRequest<void>(`/roles/${roleId}/inheritances/${inheritedRoleId}`, { method: 'DELETE' });
  },

  // ── Task 014/015: Permissions ──────────────────────────────────────────────

  async getPermissions(): Promise<Permission[]> {
    return apiRequest<Permission[]>('/roles/permissions/all');
  },

  async listPermissions(): Promise<Permission[]> {
    return this.getPermissions();
  },

  /** Task 016: Get the 14 canonical permission action types */
  async getStandardPermissionActions(): Promise<string[]> {
    return apiRequest<string[]>('/roles/permissions/standard-actions');
  },

  /** Task 015: Create a new permission with resourceType_action canonical format */
  async createPermission(data: {
    resourceType: string;
    action: PermissionAction;
    description?: string;
    category?: string;
    isSystem?: boolean;
  }): Promise<Permission> {
    return apiRequest<Permission>('/roles/permissions', { method: 'POST', body: JSON.stringify(data) });
  },

  async assignPermissionToRole(
    roleId: string,
    permissionCode: string,
    /** Task 017: Optional effect (default ALLOW) */
    options?: { effect?: PermissionEffect },
  ): Promise<void> {
    return apiRequest<void>(`/roles/${roleId}/permissions`, {
      method: 'POST',
      body: JSON.stringify({ permissionCode, effect: options?.effect ?? 'ALLOW' }),
    });
  },

  async assignPermissionsToRole(roleId: string, permissionCodes: string[]): Promise<void> {
    return apiRequest<void>(`/roles/${roleId}/permissions/bulk`, {
      method: 'POST',
      body: JSON.stringify({ permissionCodes }),
    });
  },

  async removePermissionFromRole(roleId: string, permissionCode: string): Promise<void> {
    return apiRequest<void>(`/roles/${roleId}/permissions/${encodeURIComponent(permissionCode)}`, {
      method: 'DELETE',
    });
  },

  /** Task 017: Get full role_permissions entries (with effect/conditions) */
  async getRolePermissionEntries(roleId: string): Promise<RolePermissionEntry[]> {
    return apiRequest<RolePermissionEntry[]>(`/roles/${roleId}/permission-entries`);
  },

  // ── Role Assignments ───────────────────────────────────────────────────────

  async assignRoleToUser(data: {
    userId: string;
    roleId: string;
    scopeType: ScopeType;
    scopeId?: string;
    validFrom?: string;
    validTo?: string;
  }): Promise<RoleAssignment> {
    return apiRequest<RoleAssignment>('/roles/assignments', { method: 'POST', body: JSON.stringify(data) });
  },

  async assignRole(data: {
    userId: string;
    roleId: string;
    scopeType: ScopeType;
    scopeId?: string;
    validFrom?: string;
    validTo?: string;
  }): Promise<RoleAssignment> {
    return this.assignRoleToUser(data);
  },

  async getUserAssignments(userId: string): Promise<RoleAssignment[]> {
    return apiRequest<RoleAssignment[]>(`/roles/assignments/user/${userId}`);
  },

  async revokeAssignment(id: string): Promise<void> {
    return apiRequest<void>(`/roles/assignments/${id}/revoke`, { method: 'POST' });
  },

  // ── Authorization Evaluation ───────────────────────────────────────────────

  async evaluateAccess(data: {
    userId: string;
    requiredPermission: string;
    targetScopeType?: ScopeType;
    targetScopeId?: string;
  }): Promise<AuthorizationDecision> {
    return apiRequest<AuthorizationDecision>('/roles/evaluate', { method: 'POST', body: JSON.stringify(data) });
  },

  /** Task 018: 4-tier ALLOW/DENY precedence evaluation */
  async evaluateAccessWithPrecedence(data: {
    userId: string;
    permissionCode: string;
    roleIds?: string[];
  }): Promise<PrecedenceDecision> {
    return apiRequest<PrecedenceDecision>('/roles/authorization/evaluate-precedence', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // ── Task 002: Positions & Job Titles ──────────────────────────────────────

  async getPositions(): Promise<OrganizationalPosition[]> {
    return apiRequest<OrganizationalPosition[]>('/roles/positions/all');
  },

  async createPosition(data: { code: string; title: string; departmentId?: string }): Promise<OrganizationalPosition> {
    return apiRequest<OrganizationalPosition>('/roles/positions', { method: 'POST', body: JSON.stringify(data) });
  },

  async mapPositionToRole(positionId: string, roleId: string, defaultScopeType?: ScopeType): Promise<void> {
    return apiRequest<void>(`/roles/positions/${positionId}/roles`, {
      method: 'POST',
      body: JSON.stringify({ roleId, defaultScopeType: defaultScopeType ?? 'ORGANIZATION' }),
    });
  },

  async getPositionRoleMappings(positionId: string): Promise<Role[]> {
    return apiRequest<Role[]>(`/roles/positions/${positionId}/roles`);
  },

  async setUserJobTitle(userId: string, jobTitleId: string, jobTitleName: string): Promise<void> {
    return apiRequest<void>(`/roles/users/${userId}/job-title`, {
      method: 'POST',
      body: JSON.stringify({ jobTitleId, jobTitleName }),
    });
  },

  async getUserJobTitle(userId: string): Promise<{ jobTitleId: string; jobTitleName: string } | null> {
    return apiRequest<{ jobTitleId: string; jobTitleName: string } | null>(`/roles/users/${userId}/job-title`);
  },

  // ── Audit ──────────────────────────────────────────────────────────────────

  async getAuditLogs(): Promise<RoleAuditLog[]> {
    return apiRequest<RoleAuditLog[]>('/roles/audit/logs');
  },
};
